import { useMemo, useState } from 'react';
import { Box, Typography, IconButton, Button } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { CalendarMonth } from '@mui/icons-material';
import { addDays, format, isSameDay } from 'date-fns';
import { cs } from 'date-fns/locale';

const dayLabels = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];
const minSwipeDistance = 50;

const MobileWeekStrip = ({
  weekStart,
  selectedDay,
  onSelectDay,
  onPreviousWeek,
  onNextWeek,
  onOpenMonthPicker,
  sticky = true,
  stripRef,
}) => {
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );

  const weekLabel = `${format(weekStart, 'd.M.', { locale: cs })} - ${format(
    addDays(weekStart, 6),
    'd.M.yyyy',
    {
      locale: cs,
    }
  )}`;

  const onTouchStart = (event) => {
    setTouchEndX(null);
    setTouchStartX(event.targetTouches[0].clientX);
  };

  const onTouchMove = (event) => {
    setTouchEndX(event.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;

    const distance = touchStartX - touchEndX;
    if (distance > minSwipeDistance) {
      onNextWeek();
    }
    if (distance < -minSwipeDistance) {
      onPreviousWeek();
    }
  };

  return (
    <Box
      ref={stripRef}
      className="glass-week-strip"
      sx={{
        position: sticky ? 'sticky' : 'relative',
        top: sticky ? 'var(--glass-sticky-header-offset, 0px)' : 'auto',
        zIndex: 10,
        mb: 0,
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 0.5,
        }}
      >
        <IconButton
          onClick={onPreviousWeek}
          size="small"
          aria-label="Předchozí týden"
          sx={{ color: 'var(--ink-2)' }}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
          }}
        >
          {onOpenMonthPicker ? (
            <>
              <Typography
                component="button"
                type="button"
                onClick={onOpenMonthPicker}
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: 'var(--ink)',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {weekLabel}
              </Typography>
              <IconButton
                onClick={onOpenMonthPicker}
                size="small"
                aria-label="Otevřít kalendář"
                sx={{ color: 'var(--ink-2)' }}
              >
                <CalendarMonth fontSize="small" />
              </IconButton>
            </>
          ) : (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'var(--ink)',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {weekLabel}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onNextWeek}
          size="small"
          aria-label="Další týden"
          sx={{ color: 'var(--ink-2)' }}
        >
          <KeyboardArrowRight />
        </IconButton>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.75 }}>
        {weekDays.map((day, index) => {
          const isSelected = selectedDay && isSameDay(day, selectedDay);
          const isToday = isSameDay(day, new Date());

          return (
            <Button
              key={format(day, 'yyyy-MM-dd')}
              onClick={() => onSelectDay(day)}
              variant="outlined"
              className={isSelected ? 'glass-accent-fill' : undefined}
              sx={{
                minWidth: 0,
                px: 0.5,
                py: 0.6,
                borderRadius: '8px',
                borderColor:
                  isToday && !isSelected ? 'var(--glass-border-accent)' : 'var(--glass-border)',
                color: isSelected ? 'var(--ink-on-accent)' : 'var(--ink-2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.2,
                fontSize: '0.72rem',
                lineHeight: 1.1,
                textTransform: 'none',
                background: isSelected ? undefined : 'transparent',
                boxShadow: isSelected ? '0 4px 12px rgb(240 90 90 / 0.3)' : 'none',
                '&:hover': {
                  background: isSelected ? undefined : 'rgb(255 255 255 / 0.06)',
                  borderColor: isSelected ? undefined : 'var(--glass-border-accent)',
                },
              }}
            >
              <span>{dayLabels[index]}</span>
              <span style={{ fontWeight: 700 }}>{format(day, 'd')}</span>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default MobileWeekStrip;
